import { api } from './api';

const PREFIX = '/admin/web-analytics';

// ---------------------------------------------------------------------------
// Types (mirror api/app/routers/admin_web_analytics.py responses)
// ---------------------------------------------------------------------------

export interface DailyTotals {
  day: string;
  visitors_total: number;
  visitors_bot: number;
  visitors_human: number;
  visitors_uncl: number;
  events_total: number;
  events_human: number;
  pv_human: number;
  new_visitors: number;
  returning_visitors: number;
  single_page_visitors: number;
  engaged_visitors: number;
  dwell_med_ms: number;
  dwell_p75_ms: number;
  exit0_pct: number;
}

export interface OverviewResponse {
  series: DailyTotals[];
  summary: {
    days: number;
    visitors_human: number;
    visitors_bot: number;
    pv_human: number;
    new_visitors: number;
    engaged_visitors: number;
    search_share_pct: number;
    dwell_med_ms_latest: number;
    single_page_pct: number;
  };
}

export interface BreakdownRow {
  k: string;
  visitors: number;
  events: number;
  pv: number;
}

export interface PageRow {
  path: string;
  ptype: string;
  pv: number;
  uv_daily_sum: number;
  exits: number;
  exits0: number;
}

export interface EventRow {
  event: string;
  placement: string;
  n: number;
  visitors: number;
  ok_sum: number;
  n_sum: number;
  n_ok_sum: number;
}

export interface QuizFunnel {
  placement: string;
  started: number;
  completed: number;
  questions: number;
  accuracy_pct: number;
}

export interface EventsResponse {
  cta: EventRow[];
  quiz_funnels: QuizFunnel[];
  hero_quiz: { answers: number; correct: number; completions: number };
  practice_widget: EventRow[];
  other: EventRow[];
}

export interface VitalsRow {
  day: string;
  ptype: string;
  n: number;
  lcp_p75_ms: number;
  inp_p75_ms: number;
  ttfb_p75_ms: number;
  cls_p75_milli: number;
  slow_lcp_pct: number;
}

export interface BotsResponse {
  reasons: { reason: string; visitors: number; events: number }[];
  per_day: {
    day: string;
    visitors_bot: number;
    visitors_human: number;
    events_total: number;
    events_human: number;
  }[];
}

export interface RetentionResponse {
  cohorts: { day: string; cohort: number; returned_7d: number; return_pct: number }[];
  overall: { cohort: number; returned_7d: number; return_pct: number };
}

export interface NewsQuizRow {
  day: string;
  event_type: string;
  mode: string;
  events: number;
  questions: number;
  avg_score_pct: number | null;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const getOverview = (days: number) =>
  api.get<OverviewResponse>(`${PREFIX}/overview`, { params: { days } });

export const getBreakdown = (dim: string, days: number) =>
  api.get<BreakdownRow[]>(`${PREFIX}/breakdowns`, { params: { dim, days } });

export const getPages = (days: number, limit = 30) =>
  api.get<PageRow[]>(`${PREFIX}/pages`, { params: { days, limit } });

export const getEvents = (days: number) =>
  api.get<EventsResponse>(`${PREFIX}/events`, { params: { days } });

export const getVitals = (days: number) =>
  api.get<VitalsRow[]>(`${PREFIX}/vitals`, { params: { days } });

export const getBots = (days: number) =>
  api.get<BotsResponse>(`${PREFIX}/bots`, { params: { days } });

export const getRetention = () =>
  api.get<RetentionResponse>(`${PREFIX}/retention`);

export const getLatestReport = () =>
  api.get<{ text: string }>(`${PREFIX}/report/latest`);

export const getNewsQuiz = (days: number) =>
  api.get<{ rows: NewsQuizRow[] }>(`${PREFIX}/news-quiz`, { params: { days } });
